import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { fetchGroupList, fetchRolesByAccountId } from './Groups.actions';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { translate, formatRelativeTime } from '../../shared/helpers/utils';
import styles from '../users/Users.module.scss';
import groupStyles from './Groups.module.scss';
import {
  AgTableWithRef,
  Card,
  CardContainer,
  CardContent
} from '@digicert/dcone-common-ui';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { AgGridReact } from '@ag-grid-community/react';
import type { GridReadyEvent, ColDef, IServerSideDatasource, ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faUsers } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Link, useHistory } from 'react-router-dom';
import { RouterConfig } from '../../core/utils/constants';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { Source } from './Groups.types';
import { applyGroupFilterToUserTable } from '../../shared/helpers/userTableFilters';
import { IAppState } from '../../core/store/store';
interface Group {
  id: string;
  name: string;
  source: string;
  users: number;
  services: string[];
  roles: string[];
  lastSync: string;
}

export const Groups = ({ fetchGroupList, profile }) => {
  const ref = useRef<AgGridReact>(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [gridApi, setGridApi] = useState<any>();
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string }[]>([]);
  const [availableServices, setAvailableServices] = useState<string[]>([]);
  const filtersRestoredRef = useRef(false);
  const history = useHistory();
  const accountId = useSelector((state: any) => state.profile?.data?.primary_account_id);
  const profileApplications = useSelector((state: IAppState) => state.profile?.data?.applications || {});
  const FILTER_STORAGE_KEY = 'groups-table-filters';

  const getSourceDisplayLabel = useCallback((source: string) => {
    if (source === Source.PLATFORM) return translate('accounts.details.scim.groups.digicert') as string;
    if (source === Source.SCIM) return translate('accounts.details.scim.groups.idProvider') as string;
    return source;
  }, []);

  useEffect(() => {
    const appsArray = Array.isArray(profileApplications)
      ? profileApplications
      : Object.values(profileApplications);

    if (appsArray && appsArray.length > 0) {
      const serviceNames = appsArray.map((app: any) => app.name || app.app_name || app.id);
      setAvailableServices(serviceNames);
    }
  }, [profileApplications]);

  useEffect(() => {
    if (!accountId) return;

    const fetchRoles = async () => {
      try {
        const data = await dispatch(fetchRolesByAccountId(accountId));

        if (!data || typeof data !== 'object') {
          return;
        }

        const rolesList: { value: string; label: string }[] = [];

        Object.entries(data).forEach(([serviceKey, payloadArray]) => {
          if (!Array.isArray(payloadArray) || payloadArray.length === 0) return;

          payloadArray.forEach((payload: any) => {
            const accountsList = payload?.account_roles || [];

            accountsList.forEach((account: any) => {
              const accountName = account?.account_name || account?.name;
              const accountRoles = account?.roles || [];

              accountRoles.forEach((role: any) => {
                if (role?.id && role?.display_name) {
                  const baseLabel = role.display_name || role.name || 'Unnamed Role';
                  const label = payloadArray.length > 1 && accountName
                    ? `${baseLabel} (${accountName})`
                    : baseLabel;

                  const uniqueKey = payloadArray.length > 1 && accountName
                    ? `${role.id}|${account.account_id || accountName}`
                    : role.id;

                  rolesList.push({
                    value: uniqueKey,
                    label: label
                  });
                }
              });
            });
          });
        });

        const uniqueRoles = Array.from(
          new Map(rolesList.map(item => [item.value, item])).values()
        );

        setAvailableRoles(uniqueRoles);
      } catch (err: any) {
        console.error('Error fetching roles:', err);
      }
    };

    fetchRoles();
  }, [accountId, dispatch]);

  useEffect(() => {
    filtersRestoredRef.current = false;
  }, [accountId]);

  const columns: ColDef[] = useMemo(() => [
    {
      colId: 'name',
      field: 'name',
      suppressMovable: true,
      sortable: true,
      lockVisible: true,
      headerName: translate('accounts.details.scim.groups.group') as string,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        suppressAndOrCondition: true,
        maxNumConditions: 1,
        textFormatter: (value: string) => value.trim(),
        closeOnApply: true,
        buttons: ['apply']
      },
      flex: 1,
      minWidth: 150,
      cellRenderer: (rowData: any) => {
        return (
          <Link
            className={groupStyles.groupLink} to={RouterConfig.groups.details(rowData.data.id)}>
            {rowData.data.name}
          </Link>
        );
      }
    },
    {
      colId: 'source',
      field: 'source',
      sortable: true,
      headerName: translate('accounts.details.scim.groups.managedBy') as string,
      filter: 'agSetColumnFilter',
      valueFormatter: (params: any) => getSourceDisplayLabel(params?.value),
      filterParams: {
        filterOptions: ['equals'],
        values: [
          translate('accounts.details.scim.groups.digicert') as string,
          translate('accounts.details.scim.groups.idProvider') as string
        ],
        defaultToNothingSelected: true
      },
      flex: 1,
      minWidth: 120,
      cellRenderer: (rowData: any) => {
        return <span>{getSourceDisplayLabel(rowData.data.source) as string}</span>;
      }
    },
    {
      colId: 'users',
      field: 'users',
      sortable: false,
      headerName: translate('Users') as string,
      filter: false,
      suppressMenu: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: (rowData: any) => {
        const userCount = rowData.data.users;
        const groupId = rowData.data.id;
        const groupName = rowData.data.name;
        if (userCount === 0) {
          return <span>{userCount} {translate('accounts.details.scim.groups.users')}</span>;
        }
        return (
          <Link
            to={{
              pathname: RouterConfig.urlMap.administrators,
              state: {
                groupId: groupId,
                groupName: groupName
              }
            }}
            className={groupStyles.usersLink}
            onClick={() => navigateToUserList(groupId, groupName)}
          >
            {userCount} {userCount === 1 ? translate('accounts.details.scim.groups.user') : translate('accounts.details.scim.groups.users')}
          </Link>
        );
      }
    },
    {
      colId: 'services',
      field: 'services',
      sortable: false,
      headerName: translate('accounts.details.scim.groups.services') as string,
      filter: 'agSetColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        values: availableServices,
        defaultToNothingSelected: true
      },
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (rowData: any) => {
        const hasServices = rowData.data.services && rowData.data.services.length > 0;
        return (
          <span className={groupStyles.statusIndicator}>
            {!hasServices ? (
              <>
                <FontAwesomeIcon
                  icon={faExclamationCircle as IconProp}
                  className={groupStyles.statusDot}
                />
                <span>{translate('accounts.details.scim.groups.noServicesAssigned') as string}</span>
              </>
            ) : (
              <span>{rowData.data.services.length} {rowData.data.services.length === 1 ? translate('accounts.details.scim.groups.service') : translate('accounts.details.scim.groups.services')}</span>
            )}
          </span>
        );
      }
    },
    {
      colId: 'roles',
      field: 'roles',
      sortable: false,
      headerName: translate('common.roles') as string,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: availableRoles.map(r => r.label),
        filterOptions: ['equals'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
        defaultToNothingSelected: true
      },
      flex: 1.5,
      minWidth: 200,
      cellRenderer: (rowData: any) => {
        const hasRoles = rowData.data.roles && rowData.data.roles.length > 0;
        return (
          <span className={groupStyles.statusIndicator}>
            {!hasRoles ? (
              <>
                <span className={groupStyles.statusDot}></span>
                <FontAwesomeIcon
                  icon={faExclamationCircle as IconProp}
                  className={groupStyles.statusDot}
                />
                <span>{translate('accounts.details.scim.groups.noRolesAssigned') as string}</span>
              </>
            ) : (
              <span>{rowData.data.roles.length} {rowData.data.roles.length === 1 ? translate('accounts.details.scim.groups.role') : translate('accounts.details.scim.groups.roles')}</span>
            )}
          </span>
        );
      }
    },
    {
      colId: 'lastSync',
      field: 'lastSync',
      sortable: true,
      headerName: translate('common.lastSyncUpdated') as string,
      filter: false,
      suppressMenu: true,
      flex: 1.5,
      minWidth: 180,
      cellRenderer: (rowData: any) => {
        return <span>{formatRelativeTime(rowData.data.lastSync)}</span>;
      }
    }
  ], [history, profile, profileApplications, availableRoles, availableServices, getSourceDisplayLabel]);

  // Restore persisted filters as soon as the grid is ready — independent of roles loading
  useEffect(() => {
    if (gridApi && !filtersRestoredRef.current) {
      filtersRestoredRef.current = true;
      try {
        const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
        if (savedFilters) {
          const filterModel = JSON.parse(savedFilters);
          gridApi.setFilterModel(filterModel);
        }
      } catch (error) {
        console.error('Error restoring filters from localStorage:', error);
      }
    }
  }, [gridApi, FILTER_STORAGE_KEY]);

  // Update context with roles once they are loaded
  useEffect(() => {
    if (gridApi && availableRoles.length > 0) {
      const currentContext = gridApi.getGridOption('context') || {};
      gridApi.setGridOption('context', {
        ...currentContext,
        availableRoles: availableRoles
      });
    }
  }, [availableRoles, gridApi]);

  const getApplicationNameById = useCallback((appId: string) => {
    const appsArray = Array.isArray(profileApplications)
      ? profileApplications
      : Object.values(profileApplications);
    const app = appsArray.find((a: any) => a.id === appId);
    return app ? (app.name || app.app_name || appId) : appId;
  }, [profileApplications]);

  const navigateToUserList = (groupId: string, groupName: string) => {
    applyGroupFilterToUserTable(profile?.id, groupId, groupName);
    history.push(RouterConfig.urlMap.administrators);
  };

  const handleRefresh = useCallback(() => {
    if (gridApi) {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi]);

  const handleClearAllFilters = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      setGlobalSearchTerm('');
      localStorage.removeItem(FILTER_STORAGE_KEY);
    }
  }, [gridApi]);

  const handleGlobalSearch = useCallback((searchTerm: string) => {
    setGlobalSearchTerm(searchTerm);
    if (gridApi) {
      const currentContext = gridApi.getGridOption('context') || {};
      const newContext = {
        ...currentContext,
        globalSearch: searchTerm
      };
      gridApi.setGridOption('context', newContext);
      gridApi.onFilterChanged();
    }
  }, [gridApi]);

  const handleColumnVisibilityChange = useCallback((columnId: string, visible: boolean) => {
    if (gridApi) {
      gridApi.setColumnsVisible([columnId], visible);
    }
  }, [gridApi]);

  const handleResizeColumnsToFit = useCallback(() => {
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    }
  }, [gridApi]);

  const handleFilterChanged = useCallback(() => {
    if (gridApi) {
      const filterModel = gridApi.getFilterModel();
      try {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterModel));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    }
  }, [gridApi]);

  const mapGroupData = (item: any): Group => ({
    id: item.id,
    name: item.source === Source.PLATFORM ? (translate(`groups.${item.name}`) as string) : item.name,
    source: item.source,
    users: item.user_count || 0,
    services: item.applications?.map((app: any) => app.name) || [],
    roles: item.roles?.map((role: any) => role.role_id) || [],
    lastSync: item.updated_at || item.created_at || ''
  });

  const onFilterOpened = useCallback((params: any) => {
    if (params.column.getColDef().filter !== 'agTextColumnFilter') return;
    requestAnimationFrame(() => {
      const inputs = params.eGui?.querySelectorAll('input.ag-input-field-input');
      inputs?.forEach((input: HTMLInputElement) => {
        input.value = '';
      });
    });
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent<any>) => {
    setGridApi(params.api);
    params.api.setGridOption('loading', true);


    const datasource: IServerSideDatasource = {
      getRows: async (gridParams) => {
        if (!accountId) {
          params.api.showNoRowsOverlay();
          params.api.setGridOption('loading', false);
          gridParams.success({ rowData: [], rowCount: 0 });
          return;
        }

        try {
          params.api.setGridOption('loading', true);

          const startRow = gridParams.request.startRow || 0;
          const endRow = gridParams.request.endRow || 100;
          const pageSize = endRow - startRow;

          const sortModel = gridParams.request.sortModel || [];

          const columnToApiFieldMap: Record<string, string> = {
            'name': 'name',
            'source': 'type',
            'lastSync': 'updated_at'
          };

          const filterModel: any = gridParams.request.filterModel || {};
          const context = params.api.getGridOption('context') || {};
          const globalSearch = (context.globalSearch || '').trim();

          const requestParams: any = {
            limit: pageSize,
            offset: startRow,
            accountId: accountId
          };

          if (globalSearch) {
            requestParams.groupNameLike = globalSearch;
          }

          if (filterModel.name) {
            const nameFilter = filterModel.name;
            let filterValue: any = null;

            if (typeof nameFilter === 'string' && nameFilter.trim() !== '') {
              filterValue = nameFilter.trim();
            } else if (nameFilter.filter && nameFilter.filter.trim() !== '') {
              filterValue = nameFilter.filter.trim();
            } else if (nameFilter.condition1 && nameFilter.condition1.filter && nameFilter.condition1.filter.trim() !== '') {
              filterValue = nameFilter.condition1.filter.trim();
            } else if (nameFilter.filterValue && nameFilter.filterValue.trim() !== '') {
              filterValue = nameFilter.filterValue.trim();
            }

            if (filterValue && filterValue !== '') {
              requestParams.groupNameLike = filterValue;
            }
          }

          if (filterModel.source) {
            const sourceFilter = filterModel.source;
            const getSourceApiValue = (label: string): string => {
              if (label === (translate('accounts.details.scim.groups.digicert') as string)) return Source.PLATFORM;
              if (label === (translate('accounts.details.scim.groups.idProvider') as string)) return Source.SCIM;
              return label;
            };
            if (sourceFilter.values && sourceFilter.values.length > 0) {
              const sourceValues = sourceFilter.values
                .filter((v: string) => v && v !== 'undefined')
                .map((v: string) => getSourceApiValue(v));
              if (sourceValues.length > 0) {
                requestParams.source = sourceValues.join(',');
              }
            } else if (sourceFilter.filter && sourceFilter.filter.trim() !== '' && sourceFilter.filter !== 'undefined') {
              requestParams.source = getSourceApiValue(sourceFilter.filter.trim());
            }
          }

          if (filterModel.services) {
            const servicesFilter = filterModel.services;
            if (servicesFilter.values && servicesFilter.values.length > 0) {
              const appsArray = Array.isArray(profileApplications)
                ? profileApplications
                : Object.values(profileApplications);
              const appIds = servicesFilter.values
                .filter((serviceName: string) => serviceName && serviceName !== 'undefined')
                .map((serviceName: string) => {
                  const app = appsArray.find((a: any) =>
                    (a.name === serviceName) || (a.app_name === serviceName) || (a.id === serviceName)
                  );
                  return app ? app.id : serviceName;
                });
              if (appIds.length > 0) {
                requestParams.applicationIds = appIds.join(',');
              }
            } else if (servicesFilter.filter && servicesFilter.filter.trim() !== '' && servicesFilter.filter !== 'undefined') {
              requestParams.applicationIds = servicesFilter.filter.trim();
            }
          }

          if (filterModel.roles) {
            const rolesFilter = filterModel.roles;
            if (rolesFilter.values && rolesFilter.values.length > 0) {
              const context = params.api.getGridOption('context') || {};
              const currentRoles = context.availableRoles || availableRoles;

              if (currentRoles.length > 0) {
                const roleIds = rolesFilter.values
                  .filter((roleName: string) => roleName && roleName !== 'undefined')
                  .map((roleName: string) => {
                    const role = currentRoles.find((r: any) => r.label === roleName);
                    if (!role) {
                      console.warn(`Role not found in availableRoles: ${roleName}`);
                      return null;
                    }

                    const roleValue = role.value;
                    return roleValue.includes('|') ? roleValue.split('|')[0] : roleValue;
                  })
                  .filter((id): id is string => id !== null);

                if (roleIds.length > 0) {
                  const uniqueRoleIds = Array.from(new Set(roleIds));
                  requestParams.roleIds = uniqueRoleIds.join(',');
                } else {
                  console.warn('Role filter applied but no matching role IDs found');
                }
              } else {
                console.warn('Role filter applied but availableRoles not yet loaded, skipping filter');
              }
            }
          }

          const sortParams: any = {
            limit: requestParams.limit,
            offset: requestParams.offset
          };

          if (sortModel.length > 0) {
            const sortField = columnToApiFieldMap[sortModel[0].colId] || sortModel[0].colId;
            const sortDirection = sortModel[0].sort;
            const sortValue = `${sortField}:${sortDirection}`;
            sortParams.sort = [{ columnKey: sortValue }];
          }

          const apiFilters: any = {};
          if (requestParams.groupNameLike) apiFilters.groupNameLike = requestParams.groupNameLike;
          if (requestParams.source) apiFilters.source = requestParams.source;
          if (requestParams.applicationIds) apiFilters.applicationIds = requestParams.applicationIds;
          if (requestParams.roleIds) apiFilters.roleIds = requestParams.roleIds;

          const response = await fetchGroupList(accountId, sortParams, apiFilters);
          const groupsList = response?.payload?.items || response?.items || response || [];
          const total = response?.payload?.total || response?.total || groupsList.length;

          const mappedGroups = groupsList.map(mapGroupData);

          gridParams.success({
            rowData: mappedGroups,
            rowCount: total
          });

          params.api.setGridOption('loading', false);

          if (total === 0) {
            params.api.showNoRowsOverlay();
          } else {
            params.api.hideOverlay();
          }
        } catch (error) {
          params.api.setGridOption('loading', false);
          params.api.showNoRowsOverlay();
          gridParams.fail();
        }
      }
    }
    params.api.setGridOption('serverSideDatasource', datasource);
  }, [accountId, fetchGroupList, availableRoles]);

  const primarySingleRowActions = (params?: ICellRendererParams<Group>) => {
    if (!params || !params.data) {
      return [];
    }

    const rowData = params.data;
    const actions: any[] = [];

    if (rowData.users > 0) {
      actions.push({
        id: 'view-users',
        name: translate('accounts.details.scim.groups.viewUsers') as string,
        icon: <FontAwesomeIcon icon={faUsers as IconProp} />,
        onClick: () => {
          navigateToUserList(rowData.id, rowData.name);
        },
        isVisible: true
      });
    }

    actions.push({
      id: 'view-group',
      name: translate('accounts.details.scim.groups.viewGroupDetails') as string,
      icon: <FontAwesomeIcon icon={faEye as IconProp} />,
      onClick: () => {
        history.push(RouterConfig.groups.details(rowData.id));
      },
      isVisible: true
    });
    return actions;
  };

  const getTableTopBarConfig = () => {
    return {
      LeftControls: null,
      RightControls: null,
      enableGlobalSearch: true,
      globalSearchPlaceholder: translate('accounts.details.scim.groups.searchPlaceholder') as string,
      globalSearchValue: globalSearchTerm,
      onGlobalSearch: handleGlobalSearch,
      filterButtonLabel: translate('accounts.details.scim.groups.advancedFilters') as string,
      showRefreshButton: true,
      refreshButtonLabel: translate('accounts.details.scim.groups.refresh') as string,
      onRefresh: handleRefresh,
      clearFiltersButtonLabel: translate('accounts.details.scim.groups.clearAllFilters') as string,
      onClearFilters: handleClearAllFilters,
      showActiveFilterCount: true,
      activeFilterCountLabel: translate('accounts.details.scim.groups.filtersActive') as string,
      showResizeColumnsButton: true,
      resizeColumnsButtonLabel: translate('accounts.details.scim.groups.fitColumns') as string,
      onResizeColumns: handleResizeColumnsToFit,
      searchDebounceMs: 300,
      getFilterDisplayValue: (colId: string, value: any) => {
        if (colId === 'source') {
          return getSourceDisplayLabel(value);
        }
        if (colId === 'services') {
          if (Array.isArray(value)) {
            return value.map(v => getApplicationNameById(v)).join(', ');
          }
          return getApplicationNameById(value);
        }
        return value;
      },
      columnDefs: columns
    };
  };

  // const statsData = [
  //   {
  //     label: 'Groups without roles',
  //     value: '2'
  //   }
  // ];

  return (
    <>
      <header className={groupStyles.header}>
        <h1><Translate id='Groups' /></h1>
      </header>
      {/* TODO: Will be uncommented once stats are implemented */}
      {/* <CardContainer className={groupStyles.statsContainer}>
        {statsData.map((stat, index) => (
          <Card key={index} className={groupStyles.statCard}>
            <CardContent className={groupStyles.statCardContent}>
              <div className={groupStyles.cardData}><div className={groupStyles.statLabel}>
                {stat.label}
              </div>
                <div className={groupStyles.statValue}>
                  {stat.value}
                </div>
              </div>

            </CardContent>
          </Card>
        ))}
      </CardContainer> */}

      <AgTableWithRef
        ref={ref}
        columnDefs={columns}
        rowModelType="serverSide"
        modules={[ServerSideRowModelModule]}
        licenseKey={process.env.REACT_APP_AG_GRID_LICENSE_KEY || ''}
        onGridReady={onGridReady}
        onFilterOpened={onFilterOpened}
        onFilterChanged={handleFilterChanged}
        onSortChanged={() => {
          if (gridApi) {
            gridApi.refreshServerSide({ purge: true });
          }
        }}
        shouldShowTopActionBar={true}
        getRowPrimaryActions={primarySingleRowActions}
        topBarConfig={getTableTopBarConfig()}
        className={styles.table}
        disableSSRMRowSelection={true}
        enableCellTextSelection={false}
        domLayout="normal"
        pagination={true}
        paginationPageSize={10}
        paginationPageSizeSelector={[10, 25, 50, 100]}
        paginationAutoPageSize={false}
        cacheBlockSize={10}
        maxBlocksInCache={10}
        blockLoadDebounceMillis={300}
      />
    </>
  );
};

export default connect(
  (state: IAppState) => ({
    profile: state.profile?.data
  }),
  { fetchGroupList }
)(Groups);

